import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { filter, size } from "lodash";
import { toast } from "react-toastify";

import Button from "components/Button";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import {
  changeAdministratorRole,
  getEmployeeInvitations,
  lockAdministrator,
  unlockAdministrator,
} from "actions/employerActions";

import { AGENT_LINK_ROLE_TYPES } from "statics/roles";

import "./AdministratorTable.scss";
import type { RootState } from "types/state";

interface AdministratorLink {
  role?: string;
  locked?: boolean;
}

interface OwnProps {
  user: { id: string } & Record<string, unknown>;
  link: AdministratorLink;
}

const errorSelector = createErrorSelector([
  employerConstants.GET_EMPLOYEE_INVITES,
  employerConstants.RESEND_INVITE_ADMINISTRATOR,
  employerConstants.CHANGE_ADMIN_ROLE,
  employerConstants.LOCK_ADMINISTRATOR,
  employerConstants.UNLOCK_ADMINISTRATOR,
  employerConstants.GET_ADMINISTRATORS,
]);

const isLockingSelector = createLoadingSelector(
  employerConstants.LOCK_ADMINISTRATOR
);
const isUnlockingSelector = createLoadingSelector(
  employerConstants.UNLOCK_ADMINISTRATOR
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isLocking: isLockingSelector(state),
    isUnlocking: isUnlockingSelector(state),
    currentUserId: state.user.id,
    administrators: state.employer.administrators,
  };
};

const mapDispatchToProps = {
  getEmployeeInvitations,
  lockAdministrator,
  unlockAdministrator,
  changeAdministratorRole,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

interface State {
  isLoadingByUserId: Record<string, boolean>;
  isLockingByUserId: Record<string, boolean>;
  switchingRoleAgents: Record<string, boolean>;
}

class AdministratorButtons extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoadingByUserId: {},
      isLockingByUserId: {},
      switchingRoleAgents: {},
    };
  }

  _setChangingRoleStatus = (agentId: string, isUpdating: boolean) => {
    this.setState({
      switchingRoleAgents: {
        ...this.state.switchingRoleAgents,
        [agentId]: isUpdating,
      },
    });
  };

  _changeRole = (agentId: string, role: string) => {
    this._setChangingRoleStatus(agentId, true);
    void this.props
      .changeAdministratorRole(this.props.client as ApolloClient<object>, {
        agentId,
        role,
      })
      .then((result) => {
        if (result.success) {
          toast.success("Successfully changed administrator role.");
        } else {
          toast.error("There was a problem changing administrator role.");
        }
      })
      .finally(() => {
        this._setChangingRoleStatus(agentId, false);
      });
  };

  buildLockingAnimationState = (userId: string, isLocking: boolean): State => {
    return {
      ...this.state,
      isLockingByUserId: {
        ...this.state.isLockingByUserId,
        [userId]: isLocking,
      },
    };
  };

  lockAdministrator = async (userId: string) => {
    this.setState(this.buildLockingAnimationState(userId, true), () => {
      void this.props
        .lockAdministrator(this.props.client as ApolloClient<object>, {
          agentId: userId,
        })
        .then((result) => {
          this.setState(this.buildLockingAnimationState(userId, false));
          if (result.success) {
            toast.success("Successfully locked administrator.");
          }
        });
    });
  };

  unlockAdministrator = (userId: string) => {
    this.setState(this.buildLockingAnimationState(userId, true), () => {
      void this.props
        .unlockAdministrator(this.props.client as ApolloClient<object>, {
          agentId: userId,
        })
        .then((result) => {
          this.setState(this.buildLockingAnimationState(userId, false));
          if (result.success) {
            toast.success("Successfully unlocked administrator.");
          }
        });
    });
  };

  _hasMoreThanOneActiveAdmin = () => {
    const unlockedAdmins = filter(
      this.props.administrators,
      (admin) => !admin.link.locked
    );

    return size(unlockedAdmins) > 1;
  };

  _getActionsButtons = () => {
    const agent = this.props.user;
    const isCurrentAdmin = agent.id === this.props.currentUserId;

    const link = this.props.link;
    const isAdmin = link.role === AGENT_LINK_ROLE_TYPES.ADMIN;

    const lockButton = link.locked ? (
      <Button
        btnLabel={`Unlock`}
        size="sm"
        withArrow
        name="submit"
        loading={this.state.isLockingByUserId[agent.id]}
        onClick={() => this.unlockAdministrator(agent.id)}
      />
    ) : (
      <Button
        btnLabel={`Lock`}
        size="sm"
        withArrow
        name="submit"
        color="red"
        onClick={() => this.lockAdministrator(agent.id)}
        disabled={
          isAdmin && (isCurrentAdmin || !this._hasMoreThanOneActiveAdmin())
        }
        loading={this.state.isLockingByUserId[agent.id]}
      />
    );

    const convertButton = isAdmin ? (
      <Button
        style={{ padding: "6px 30px 6px 10px" }}
        btnLabel="Make Admin"
        color="action"
        name="submit"
        size="sm"
        withArrow
        disabled={isCurrentAdmin || !this._hasMoreThanOneActiveAdmin()}
        loading={this.state.switchingRoleAgents[agent.id]}
        onClick={() => this._changeRole(agent.id, AGENT_LINK_ROLE_TYPES.AGENT)}
      />
    ) : (
      <Button
        btnLabel="Make Owner"
        size="sm"
        color="action"
        name="submit"
        withArrow
        loading={this.state.switchingRoleAgents[agent.id]}
        onClick={() => this._changeRole(agent.id, AGENT_LINK_ROLE_TYPES.ADMIN)}
      />
    );

    return (
      <>
        <div style={{ display: "flex" }}>
          {!link.locked && convertButton}
          <span style={{ paddingLeft: 10 }}> {lockButton}</span>
        </div>
      </>
    );
  };

  render() {
    return this._getActionsButtons();
  }
}

export default connector(withApollo<Props>(AdministratorButtons));
